import { Box, Flex, Image, Text, useColorModeValue } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import React from 'react';

import { route } from 'nextjs-routes';

import config from 'configs/app';
import throwOnResourceLoadError from 'lib/errors/throwOnResourceLoadError';
import { useI18n } from 'lib/i18n/context';
import getQueryParamString from 'lib/router/getQueryParamString';
import { currencyUnits } from 'lib/units';
import ServiceDegradationWarning from 'ui/shared/alerts/ServiceDegradationWarning';
import isCustomAppError from 'ui/shared/AppError/isCustomAppError';
import CurrencyValue from 'ui/shared/CurrencyValue';
import DataFetchAlert from 'ui/shared/DataFetchAlert';
import AddressEntity from 'ui/shared/entities/address/AddressEntity';
import TxEntity from 'ui/shared/entities/tx/TxEntity';
import Hint from 'ui/shared/Hint';
import LinkInternal from 'ui/shared/links/LinkInternal';
import NativeTokenIcon from 'ui/shared/NativeTokenIcon';

import AddressCounterItem from './details/AddressCounterItem';
import AddressNameInfo from './details/AddressNameInfo';
import AddressNetWorth from './details/AddressNetWorth';
import { ADDRESS_DETAILS_KEYS } from './i18n';
import TokenSelect from './tokenSelect/TokenSelect';
import useAddressCountersQuery from './utils/useAddressCountersQuery';
import type { AddressQuery } from './utils/useAddressQuery';

interface Props {
  addressQuery: AddressQuery;
  scrollRef?: React.RefObject<HTMLDivElement>;
}

const AddressDetails = ({ addressQuery, scrollRef }: Props) => {
  const router = useRouter();
  const { t } = useI18n();

  const addressHash = getQueryParamString(router.query.hash);

  const countersQuery = useAddressCountersQuery({
    hash: addressHash,
    addressQuery,
  });

  const handleCounterItemClick = React.useCallback(() => {
    window.setTimeout(() => {
      scrollRef?.current?.scrollIntoView({ behavior: 'smooth' });
    }, 500);
  }, [ scrollRef ]);

  const cardBg = useColorModeValue('#FFFFFF', '#111111');
  const borderColor = useColorModeValue('#E9ECEF', '#222222');
  const primaryText = useColorModeValue('#000000', '#FFFFFF');
  const secondaryText = useColorModeValue('#616161', '#999999');
  const blockCubeSrc = useColorModeValue('/static/address/block-cube-light.svg', '/static/address/block-cube.svg');
  const nativeBalanceIconSrc = config.chain.currency.symbol === 'BOT' ? '/static/address/bot-balance-icon.svg' : null;

  const error404Data = React.useMemo(() => ({
    hash: addressHash || '',
    is_contract: false,
    implementations: null,
    token: null,
    watchlist_address_id: null,
    watchlist_names: null,
    creation_transaction_hash: null,
    block_number_balance_updated_at: null,
    name: null,
    exchange_rate: null,
    coin_balance: null,
    has_tokens: true,
    has_token_transfers: true,
    has_validated_blocks: false,
    filecoin: undefined,
    creator_filecoin_robust_address: null,
    creator_address_hash: null,
  }), [ addressHash ]);

  // error handling (except 404 codes)
  if (addressQuery.isError) {
    if (isCustomAppError(addressQuery.error)) {
      const is404Error = addressQuery.isError && 'status' in addressQuery.error && addressQuery.error.status === 404;
      if (!is404Error) {
        throwOnResourceLoadError(addressQuery);
      }
    } else {
      return <DataFetchAlert/>;
    }
  }

  const data = addressQuery.isError ? error404Data : addressQuery.data;

  if (!data) {
    return null;
  }

  const isLoading = addressQuery.isPlaceholderData;
  const creatorAddressHash = data.creator_address_hash || undefined;

  const cardStyle = {
    bg: cardBg,
    border: '1px solid',
    borderColor: borderColor,
    borderRadius: '8px',
    p: '16px',
    flex: 1,
  };

  const sectionTitleSx = {
    fontFamily: '\'Space Grotesk\', var(--chakra-fonts-heading)',
    fontWeight: 700,
    fontSize: '14px',
    color: primaryText,
  };

  const labelSx = {
    fontFamily: '\'Space Grotesk\', var(--chakra-fonts-body)',
    fontWeight: 500,
    fontSize: '14px',
    color: secondaryText,
    textTransform: 'uppercase' as const,
  };

  const valueSx = {
    fontFamily: '\'Oxanium\', var(--chakra-fonts-body)',
    fontWeight: 500,
    fontSize: '16px',
    color: primaryText,
  };

  return (
    <>
      { addressQuery.isDegradedData && <ServiceDegradationWarning isLoading={ isLoading } mb={ 6 }/> }
      <Flex gap="16px" mb="16px" flexDirection={{ base: 'column', lg: 'row' }}>
        { /* Card 1 — Overview */ }
        <Flex { ...cardStyle } direction="column" gap="16px">
          <Text { ...sectionTitleSx }>{ t(ADDRESS_DETAILS_KEYS.sections.overview) }</Text>

          <AddressNameInfo data={ data } isLoading={ isLoading }/>

          { /* Creator */ }
          { data.is_contract && data.creation_transaction_hash && creatorAddressHash && (
            <Box alignSelf="stretch">
              <Flex alignItems="center" gap="4px" mb="4px">
                <Text { ...labelSx }>{ t(ADDRESS_DETAILS_KEYS.fields.creator) }</Text>
                <Hint label={ t(ADDRESS_DETAILS_KEYS.tooltip.creator) } isLoading={ isLoading } iconColor={ secondaryText }/>
              </Flex>
              <Flex
                alignItems="center"
                gap="8px"
                flexWrap="wrap"
                { ...valueSx }
                sx={{
                  '& a': {
                    color: 'inherit',
                  },
                  '& a:hover': {
                    color: 'inherit',
                  },
                }}
              >
                <AddressEntity
                  address={{ hash: creatorAddressHash, filecoin: { robust: data.creator_filecoin_robust_address } }}
                  truncation="constant"
                  noIcon
                />
                <Text whiteSpace="pre" color={ secondaryText }> { t(ADDRESS_DETAILS_KEYS.inline.atTxn) } </Text>
                <TxEntity hash={ data.creation_transaction_hash } truncation="constant" noIcon noCopy={ false }/>
              </Flex>
            </Box>
          ) }

          { /* Tokens — always shown; 0 when none */ }
          <Box alignSelf="stretch">
            <Flex alignItems="center" gap="4px" mb="4px">
              <Text { ...labelSx }>{ t(ADDRESS_DETAILS_KEYS.fields.tokens) }</Text>
              <Hint label={ t(ADDRESS_DETAILS_KEYS.tooltip.tokens) } isLoading={ isLoading } iconColor={ secondaryText }/>
            </Flex>
            <Box { ...valueSx }>
              { addressQuery.data ?
                <TokenSelect onClick={ handleCounterItemClick }/> :
                <Text>0</Text> }
            </Box>
          </Box>
        </Flex>

        { /* Card 2 — More Info */ }
        <Flex { ...cardStyle } direction="column" gap="16px" alignSelf="stretch">
          <Text { ...sectionTitleSx }>{ t(ADDRESS_DETAILS_KEYS.sections.moreInfo) }</Text>

          { /* Balance */ }
          <Flex alignItems="center" alignSelf="stretch" gap="8px">
            <Box flex="none">
              <Flex alignItems="center" gap="4px" mb="4px">
                <Text { ...labelSx }>{ t(ADDRESS_DETAILS_KEYS.fields.balance) }</Text>
                <Hint label={ t(ADDRESS_DETAILS_KEYS.tooltip.balance, { symbol: currencyUnits.ether }) } isLoading={ isLoading } iconColor={ secondaryText }/>
              </Flex>
              <Flex alignItems="center" gap="8px" { ...valueSx }>
                { nativeBalanceIconSrc && !isLoading ? (
                  <Image boxSize={ 5 } src={ nativeBalanceIconSrc } alt={ t('common.botLogo') } borderRadius="base"/>
                ) : (
                  <NativeTokenIcon boxSize={ 5 } isLoading={ isLoading }/>
                ) }
                <CurrencyValue
                  value={ data.coin_balance || '0' }
                  decimals={ String(config.chain.currency.decimals) }
                  currency={ currencyUnits.ether }
                  accuracy={ 8 }
                  isLoading={ isLoading }
                />
              </Flex>
            </Box>
          </Flex>

          { /* Net Worth */ }
          { (config.features.multichainButton.isEnabled || (data.exchange_rate && data.has_tokens)) && (
            <Flex alignItems="center" alignSelf="stretch" gap="16px">
              <Box>
                <Flex alignItems="center" gap="4px" mb="4px">
                  <Text { ...labelSx }>{ t(ADDRESS_DETAILS_KEYS.fields.netWorth) }</Text>
                  <Hint label={ t(ADDRESS_DETAILS_KEYS.tooltip.netWorth) } isLoading={ isLoading } iconColor={ secondaryText }/>
                </Flex>
                <Flex alignItems="baseline" gap="4px">
                  <Box { ...valueSx }>
                    <AddressNetWorth addressData={ addressQuery.data } addressHash={ addressHash } isLoading={ isLoading }/>
                  </Box>
                </Flex>
              </Box>
            </Flex>
          ) }

          { /* Last balance update */ }
          { data.block_number_balance_updated_at && (
            <Box alignSelf="stretch">
              <Flex alignItems="center" gap="4px" mb="4px">
                <Text { ...labelSx }>{ t(ADDRESS_DETAILS_KEYS.fields.lastBalanceUpdate) }</Text>
                <Hint label={ t(ADDRESS_DETAILS_KEYS.tooltip.lastBalanceUpdate) } isLoading={ isLoading } iconColor={ secondaryText }/>
              </Flex>
              <Flex alignItems="center" gap="8px" { ...valueSx }>
                <Image src={ blockCubeSrc } alt="" width="20px" height="20px" flexShrink={ 0 }/>
                <LinkInternal
                  href={ route({ pathname: '/block/[height_or_hash]', query: { height_or_hash: String(data.block_number_balance_updated_at) } }) }
                >
                  <Text { ...valueSx }>{ data.block_number_balance_updated_at }</Text>
                </LinkInternal>
              </Flex>
            </Box>
          ) }
        </Flex>

        { /* Card 3 — Blockchain Info */ }
        <Flex { ...cardStyle } direction="column" gap="16px" alignSelf="stretch">
          <Text { ...sectionTitleSx }>{ t(ADDRESS_DETAILS_KEYS.sections.blockchainInfo) }</Text>

          { /* Transactions */ }
          <Flex alignItems="center" alignSelf="stretch" gap="8px">
            <Box>
              <Flex alignItems="center" gap="4px" mb="4px">
                <Text { ...labelSx }>{ t(ADDRESS_DETAILS_KEYS.fields.transactions) }</Text>
                <Hint
                  label={ t(ADDRESS_DETAILS_KEYS.tooltip.transactions) }
                  isLoading={ isLoading || countersQuery.isPlaceholderData }
                  iconColor={ secondaryText }
                />
              </Flex>
              <Box { ...valueSx }>
                { addressQuery.data ? (
                  <AddressCounterItem
                    prop="transactions_count"
                    query={ countersQuery }
                    address={ data.hash }
                    onClick={ handleCounterItemClick }
                    isAddressQueryLoading={ isLoading }
                    isDegradedData={ addressQuery.isDegradedData }
                    noLink
                  />
                ) : 0 }
              </Box>
            </Box>
          </Flex>

          { /* Transfers */ }
          { data.has_token_transfers && (
            <Box alignSelf="stretch">
              <Flex alignItems="center" gap="4px" mb="4px">
                <Text { ...labelSx }>{ t(ADDRESS_DETAILS_KEYS.fields.transfers) }</Text>
                <Hint
                  label={ t(ADDRESS_DETAILS_KEYS.tooltip.transfers) }
                  isLoading={ isLoading || countersQuery.isPlaceholderData }
                  iconColor={ secondaryText }
                />
              </Flex>
              <Box { ...valueSx }>
                { addressQuery.data ? (
                  <AddressCounterItem
                    prop="token_transfers_count"
                    query={ countersQuery }
                    address={ data.hash }
                    onClick={ handleCounterItemClick }
                    isAddressQueryLoading={ isLoading }
                    isDegradedData={ addressQuery.isDegradedData }
                  />
                ) : 0 }
              </Box>
            </Box>
          ) }

          { /* Gas Used */ }
          { countersQuery.data?.gas_usage_count && (
            <Box alignSelf="stretch">
              <Flex alignItems="center" gap="4px" mb="4px">
                <Text { ...labelSx }>{ t(ADDRESS_DETAILS_KEYS.fields.gasUsed) }</Text>
                <Hint label={ t(ADDRESS_DETAILS_KEYS.tooltip.gasUsed) } isLoading={ isLoading || countersQuery.isPlaceholderData } iconColor={ secondaryText }/>
              </Flex>
              <Box { ...valueSx }>
                { addressQuery.data ? (
                  <AddressCounterItem
                    prop="gas_usage_count"
                    query={ countersQuery }
                    address={ data.hash }
                    onClick={ handleCounterItemClick }
                    isAddressQueryLoading={ isLoading }
                    isDegradedData={ addressQuery.isDegradedData }
                  />
                ) : 0 }
              </Box>
            </Box>
          ) }
        </Flex>
      </Flex>
    </>
  );
};

export default React.memo(AddressDetails);
